// Estado del chat interno: la lista de conversaciones, la que está abierta y la conexión
// en vivo. Lo usa ChatInterno.jsx.
//
// - La lista y el historial salen de la API REST; lo que pasa después llega por la
//   conexión en vivo y se aplica encima. Al (re)conectarse se vuelven a pedir, por si
//   algo pasó mientras estaba desconectado.
// - Un mensaje propio aparece de inmediato ("enviando") y se cambia por el guardado
//   cuando el servidor lo confirma. Si falla, queda marcado para reintentarlo.
// - Un archivo se sube directo al almacén del chat (Cloudflare) con un permiso del API y
//   luego se envía como mensaje. Mientras sube se ve con su vista previa y su avance.
// - Se marca leído solo lo que de verdad se ve: el panel abierto, en esa conversación
//   y con la pestaña del navegador al frente.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import chatServiceGlobal from "services/chatService/chat.service";
import { getSessionToken } from "services/http.services";
import { crearConexionChat } from "./conexionChat";
import { ahoraSinZona } from "./formato";
import { categoriaDe, comprimirImagen, subirArchivo } from "./archivos";

const ESCRIBIENDO_DURA_MS = 5000;
const AVISO_ESCRIBIENDO_CADA_MS = 3000;
// Si la conexión en vivo no está, la lista (y el contador) se refresca así.
const INTERVALO_SIN_CONEXION_MS = 60 * 1000;
const ESPERA_RECARGA_MS = 300;

const documentoVisible = () => typeof document === "undefined" || document.visibilityState !== "hidden";

// Los guardados en el orden en que se numeraron; los que aún se envían, al final.
const ordenar = (mensajes) => [
  ...mensajes.filter((mensaje) => !mensaje.local).sort((a, b) => a.id - b.id),
  ...mensajes.filter((mensaje) => mensaje.local),
];

const conMensaje = (mensajes, mensaje) =>
  (mensajes.some((existente) => existente.id === mensaje.id) ? mensajes : ordenar([...mensajes, mensaje]));

const ultimoIdGuardado = (mensajes) =>
  mensajes.reduce((maximo, mensaje) => (!mensaje.local && mensaje.id > maximo ? mensaje.id : maximo), 0);

/**
 * A dónde va lo que se escribe en el hilo: la conversación o, si es una directa que aún no
 * existe, la persona.
 */
const destinoDe = (hilo) => (hilo
  ? { conversacionId: hilo.conversacionId, destinatarioId: hilo.conversacionId ? null : hilo.destinatario?.id ?? null }
  : null);

const esDelDestino = (destino) => (previo) => Boolean(previo) && (destino.conversacionId
  ? previo.conversacionId === destino.conversacionId
  : !previo.conversacionId && previo.destinatario?.id === destino.destinatarioId);

// La vista previa de un archivo que se sube ocupa memoria hasta que se suelta.
const soltarVistaPrevia = (mensaje) => {
  if (mensaje?.adjunto?.local && mensaje.adjunto.url && typeof URL.revokeObjectURL === "function") {
    URL.revokeObjectURL(mensaje.adjunto.url);
  }
};

/** La conversación sube a la cabeza de la lista con su nuevo último mensaje. */
const conUltimoMensaje = (conversaciones, mensaje, { sumarNoLeido }) => {
  const actual = conversaciones.find((conversacion) => conversacion.id === mensaje.conversacionId);
  if (!actual || (actual.ultimoMensaje?.id || 0) >= mensaje.id) return conversaciones;
  const actualizada = {
    ...actual,
    ultimoMensaje: mensaje,
    fecha: mensaje.fecha,
    noLeidos: actual.noLeidos + (sumarNoLeido ? 1 : 0),
  };
  return [actualizada, ...conversaciones.filter((conversacion) => conversacion.id !== mensaje.conversacionId)];
};

export default function useChat({ usuarioId, visible, alMensajeNuevo, crearConexion = crearConexionChat }) {
  const [conversaciones, setConversaciones] = useState([]);
  const [listaCargada, setListaCargada] = useState(false);
  const [estado, setEstado] = useState("conectando");
  const [enLinea, setEnLinea] = useState(() => new Set());
  // conversacionId -> { usuarioId, nombre, hasta }
  const [escribiendo, setEscribiendo] = useState({});
  // La conversación abierta. Sin conversacionId es una directa que aún no existe: se crea
  // con el primer mensaje a `destinatario`.
  const [hilo, setHilo] = useState(null);
  const [usuarios, setUsuarios] = useState(null);

  const conexionRef = useRef(null);
  const hiloRef = useRef(hilo);
  const conversacionesRef = useRef(conversaciones);
  const visibleRef = useRef(visible);
  const alMensajeNuevoRef = useRef(alMensajeNuevo);
  const recargaRef = useRef(null);
  const ultimoAvisoEscribiendoRef = useRef(0);
  const siguienteLocalRef = useRef(1);
  hiloRef.current = hilo;
  conversacionesRef.current = conversaciones;
  visibleRef.current = visible;
  alMensajeNuevoRef.current = alMensajeNuevo;

  /** @returns {Promise<Array|null>} la lista nueva, o null si no se pudo consultar */
  const cargarConversaciones = useCallback(async () => {
    const respuesta = await chatServiceGlobal.conversaciones();
    if (respuesta?.error) return null;
    const lista = Array.isArray(respuesta?.data) ? respuesta.data : [];
    setConversaciones(lista);
    setListaCargada(true);
    return lista;
  }, []);

  // Varios avisos seguidos (crear un grupo manda dos) se resuelven con una sola consulta.
  const programarRecarga = useCallback(() => {
    clearTimeout(recargaRef.current);
    recargaRef.current = setTimeout(cargarConversaciones, ESPERA_RECARGA_MS);
  }, [cargarConversaciones]);

  const marcarLeidoLoVisible = useCallback(() => {
    const actual = hiloRef.current;
    if (!actual?.conversacionId || actual.cargando || !visibleRef.current || !documentoVisible()) return;
    const ultimo = ultimoIdGuardado(actual.mensajes);
    if (!ultimo) return;
    const conversacion = conversacionesRef.current.find((item) => item.id === actual.conversacionId);
    const yo = actual.participantes.find((participante) => participante.id === usuarioId);
    if ((yo?.ultimoLeido || 0) >= ultimo && !conversacion?.noLeidos) return;

    conexionRef.current?.leer(actual.conversacionId, ultimo);
    setConversaciones((previas) => previas.map((item) => (item.id === actual.conversacionId ? { ...item, noLeidos: 0 } : item)));
    setHilo((previo) => (previo?.conversacionId === actual.conversacionId
      ? {
        ...previo,
        participantes: previo.participantes.map((participante) => (participante.id === usuarioId
          ? { ...participante, ultimoLeido: Math.max(participante.ultimoLeido || 0, ultimo) }
          : participante)),
      }
      : previo));
  }, [usuarioId]);

  const cargarHilo = useCallback(async (conversacionId) => {
    const respuesta = await chatServiceGlobal.mensajes({ conversacionId });
    setHilo((previo) => {
      if (previo?.conversacionId !== conversacionId) return previo;
      if (respuesta?.error) return { ...previo, cargando: false, error: respuesta.error };
      const { mensajes = [], hayMas = false, participantes = [] } = respuesta?.data || {};
      // Lo que se está enviando en este momento no se pierde al recargar.
      const locales = previo.mensajes.filter((mensaje) => mensaje.local);
      return { ...previo, mensajes: ordenar([...mensajes, ...locales]), hayMas, participantes, cargando: false, error: null };
    });
  }, []);

  // Tras cargar el historial (o volver al frente), lo visible queda leído.
  useEffect(() => {
    marcarLeidoLoVisible();
  }, [hilo?.conversacionId, hilo?.cargando, visible, marcarLeidoLoVisible]);

  useEffect(() => {
    const alCambiarVisibilidad = () => marcarLeidoLoVisible();
    document.addEventListener("visibilitychange", alCambiarVisibilidad);
    return () => document.removeEventListener("visibilitychange", alCambiarVisibilidad);
  }, [marcarLeidoLoVisible]);

  const abrir = useCallback((conversacion) => {
    setHilo({
      conversacionId: conversacion.id,
      destinatario: null,
      mensajes: [],
      hayMas: false,
      participantes: conversacion.participantes || [],
      cargando: true,
      error: null,
    });
    cargarHilo(conversacion.id);
  }, [cargarHilo]);

  /** La directa con esa persona: la que ya existe o una nueva que se crea al escribir. */
  const abrirCon = useCallback((usuario) => {
    const existente = conversacionesRef.current.find((item) => item.tipo === "directa" && item.contactoId === usuario.id);
    if (existente) {
      abrir(existente);
      return;
    }
    setHilo({
      conversacionId: null,
      destinatario: usuario,
      mensajes: [],
      hayMas: false,
      participantes: [],
      cargando: false,
      error: null,
    });
  }, [abrir]);

  const cerrarHilo = useCallback(() => setHilo(null), []);

  const cargarAnteriores = useCallback(async () => {
    const actual = hiloRef.current;
    const primero = actual?.mensajes.find((mensaje) => !mensaje.local);
    if (!actual?.conversacionId || !primero || actual.cargandoAnteriores) return;
    setHilo((previo) => ({ ...previo, cargandoAnteriores: true }));
    const respuesta = await chatServiceGlobal.mensajes({ conversacionId: actual.conversacionId, antesDe: primero.id });
    setHilo((previo) => {
      if (previo?.conversacionId !== actual.conversacionId) return previo;
      if (respuesta?.error) return { ...previo, cargandoAnteriores: false };
      const anteriores = respuesta?.data?.mensajes || [];
      return {
        ...previo,
        mensajes: ordenar([...anteriores.filter((mensaje) => !previo.mensajes.some((m) => m.id === mensaje.id)), ...previo.mensajes]),
        hayMas: Boolean(respuesta?.data?.hayMas),
        cargandoAnteriores: false,
      };
    });
  }, []);

  const cambiarProvisional = useCallback((idLocal, cambios) => {
    setHilo((previo) => (previo
      ? { ...previo, mensajes: previo.mensajes.map((item) => (item.id === idLocal ? { ...item, ...cambios } : item)) }
      : previo));
  }, []);

  const nuevoProvisional = useCallback((datos) => {
    const idLocal = `local-${siguienteLocalRef.current}`;
    siguienteLocalRef.current += 1;
    return { id: idLocal, local: true, usuarioId, tipo: "texto", texto: "", fecha: ahoraSinZona(), estado: "enviando", ...datos };
  }, [usuarioId]);

  /** El provisional se cambia por el mensaje guardado; una directa nueva pasa a existir. */
  const confirmar = useCallback((idLocal, mensaje, destino) => {
    const eraNueva = !destino.conversacionId;
    const delDestino = esDelDestino(destino);
    soltarVistaPrevia(hiloRef.current?.mensajes.find((item) => item.id === idLocal));
    setHilo((previo) => {
      if (!delDestino(previo) && previo?.conversacionId !== mensaje.conversacionId) return previo;
      return {
        ...previo,
        conversacionId: mensaje.conversacionId,
        mensajes: conMensaje(previo.mensajes.filter((item) => item.id !== idLocal), mensaje),
      };
    });
    // Lo que siga en este envío (más archivos) ya va a la conversación.
    Object.assign(destino, { conversacionId: mensaje.conversacionId, destinatarioId: null });
    if (conversacionesRef.current.some((item) => item.id === mensaje.conversacionId)) {
      setConversaciones((previas) => conUltimoMensaje(previas, mensaje, { sumarNoLeido: false }));
    } else {
      programarRecarga();
    }
    // Era una directa sin abrir: si ya tenía historia (la lista no la tenía aún), se trae.
    if (eraNueva) cargarHilo(mensaje.conversacionId);
  }, [programarRecarga, cargarHilo]);

  const enviar = useCallback(async (texto) => {
    const destino = destinoDe(hiloRef.current);
    const limpio = (texto || "").trim();
    if (!destino || !limpio) return;

    const provisional = nuevoProvisional({ texto: limpio });
    setHilo((previo) => (esDelDestino(destino)(previo) ? { ...previo, mensajes: [...previo.mensajes, provisional] } : previo));
    try {
      const mensaje = await conexionRef.current.enviarMensaje({ ...destino, texto: limpio });
      confirmar(provisional.id, mensaje, destino);
    } catch (error) {
      cambiarProvisional(provisional.id, { estado: "error", error: error?.message || "No se pudo enviar" });
    }
  }, [nuevoProvisional, confirmar, cambiarProvisional]);

  // Comprimir (si es foto) -> permiso del API -> subir directo al almacén -> enviar.
  const subirYEnviar = useCallback(async (provisional, destino) => {
    try {
      const archivo = await comprimirImagen(provisional.archivo);
      const preparado = await chatServiceGlobal.prepararAdjunto({ ...destino, nombre: archivo.name, tipo: archivo.type, tamano: archivo.size });
      if (preparado?.error) throw new Error(preparado.error);
      const { clave, urlSubida, tipo } = preparado.data;
      let ultimoAvance = 0;
      await subirArchivo(urlSubida, archivo, {
        tipo,
        alProgreso: (avance) => {
          // Sin repintar por cada pedacito.
          if (avance - ultimoAvance < 0.05 && avance < 1) return;
          ultimoAvance = avance;
          cambiarProvisional(provisional.id, { progreso: avance });
        },
      });
      cambiarProvisional(provisional.id, { estado: "enviando", progreso: 1 });
      const mensaje = await conexionRef.current.enviarMensaje({
        ...destino,
        texto: provisional.texto,
        adjunto: { clave, duracion: provisional.adjunto.duracion },
      });
      confirmar(provisional.id, mensaje, destino);
    } catch (error) {
      cambiarProvisional(provisional.id, { estado: "error", error: error?.message || "No se pudo enviar el archivo" });
    }
  }, [cambiarProvisional, confirmar]);

  /**
   * Cada archivo va en su propio mensaje; el texto, con el primero. Acepta File o
   * { archivo, duracion } (notas de voz).
   */
  const enviarArchivos = useCallback(async (archivos, texto = "") => {
    const destino = destinoDe(hiloRef.current);
    if (!destino || !archivos?.length) return;
    const leyenda = (texto || "").trim();
    const provisionales = archivos.map((item, indice) => {
      const { archivo, duracion = null } = item?.archivo ? item : { archivo: item };
      const categoria = categoriaDe(archivo.type);
      return nuevoProvisional({
        texto: indice === 0 ? leyenda : "",
        estado: "subiendo",
        progreso: 0,
        archivo,
        adjunto: {
          local: true,
          nombre: archivo.name,
          tipo: archivo.type,
          categoria,
          tamano: archivo.size,
          duracion,
          // Se ve (o se oye) mientras sube, sin esperar al almacén.
          url: categoria !== "documento" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(archivo) : null,
        },
      });
    });
    setHilo((previo) => (esDelDestino(destino)(previo) ? { ...previo, mensajes: [...previo.mensajes, ...provisionales] } : previo));
    // Uno tras otro: llegan en el orden en que se eligieron.
    for (const provisional of provisionales) {
      // eslint-disable-next-line no-await-in-loop
      await subirYEnviar(provisional, destino);
    }
  }, [nuevoProvisional, subirYEnviar]);

  const descartar = useCallback((idLocal) => {
    soltarVistaPrevia(hiloRef.current?.mensajes.find((item) => item.id === idLocal));
    setHilo((previo) => (previo ? { ...previo, mensajes: previo.mensajes.filter((item) => item.id !== idLocal) } : previo));
  }, []);

  const reintentar = useCallback((mensajeLocal) => {
    descartar(mensajeLocal.id);
    if (mensajeLocal.archivo) {
      enviarArchivos([{ archivo: mensajeLocal.archivo, duracion: mensajeLocal.adjunto?.duracion ?? null }], mensajeLocal.texto);
    } else {
      enviar(mensajeLocal.texto);
    }
  }, [descartar, enviar, enviarArchivos]);

  const avisarEscribiendo = useCallback(() => {
    const actual = hiloRef.current;
    const ahora = Date.now();
    if (!actual?.conversacionId || ahora - ultimoAvisoEscribiendoRef.current < AVISO_ESCRIBIENDO_CADA_MS) return;
    ultimoAvisoEscribiendoRef.current = ahora;
    conexionRef.current?.escribiendo(actual.conversacionId);
  }, []);

  const cargarUsuarios = useCallback(async () => {
    const respuesta = await chatServiceGlobal.usuarios();
    if (!respuesta?.error) setUsuarios(Array.isArray(respuesta?.data) ? respuesta.data : []);
  }, []);

  /** @returns {Promise<String|null>} el error, o null si salió bien */
  const crearGrupo = useCallback(async ({ nombre, usuarioIds }) => {
    const respuesta = await chatServiceGlobal.crearGrupo({ nombre, usuarioIds });
    if (respuesta?.error) return respuesta.error;
    const lista = await cargarConversaciones();
    const creada = (lista || []).find((item) => item.id === respuesta?.data?.id);
    if (creada) abrir(creada);
    return null;
  }, [abrir, cargarConversaciones]);

  const agregarAGrupo = useCallback(async (usuarioIds) => {
    const conversacionId = hiloRef.current?.conversacionId;
    const respuesta = await chatServiceGlobal.agregarAGrupo({ conversacionId, usuarioIds });
    if (respuesta?.error) return respuesta.error;
    cargarHilo(conversacionId);
    programarRecarga();
    return null;
  }, [cargarHilo, programarRecarga]);

  const salirDeGrupo = useCallback(async () => {
    const conversacionId = hiloRef.current?.conversacionId;
    const respuesta = await chatServiceGlobal.salirDeGrupo({ conversacionId });
    if (respuesta?.error) return respuesta.error;
    setHilo(null);
    setConversaciones((previas) => previas.filter((item) => item.id !== conversacionId));
    return null;
  }, []);

  // --- Lo que llega en vivo ---

  const recibirMensaje = (mensaje) => {
    const actual = hiloRef.current;
    const enElHilo = actual?.conversacionId === mensaje.conversacionId;
    const propio = mensaje.usuarioId === usuarioId;
    const loVe = enElHilo && visibleRef.current && documentoVisible();

    if (enElHilo) {
      setHilo((previo) => (previo?.conversacionId === mensaje.conversacionId
        ? { ...previo, mensajes: conMensaje(previo.mensajes, mensaje) }
        : previo));
    }
    setEscribiendo((previo) => {
      if (previo[mensaje.conversacionId]?.usuarioId !== mensaje.usuarioId) return previo;
      const { [mensaje.conversacionId]: quitado, ...resto } = previo;
      return resto;
    });

    const conversacion = conversacionesRef.current.find((item) => item.id === mensaje.conversacionId);
    const cuenta = !propio && mensaje.tipo === "texto";
    if (conversacion) {
      setConversaciones((previas) => conUltimoMensaje(previas, mensaje, { sumarNoLeido: cuenta && !loVe }));
    } else {
      programarRecarga();
    }
    if (loVe && !propio) conexionRef.current?.leer(mensaje.conversacionId, mensaje.id);
    if (cuenta && !loVe) alMensajeNuevoRef.current?.(mensaje, conversacion || null);
  };

  const recibirLeido = ({ conversacionId, usuarioId: quien, ultimoLeido }) => {
    if (quien === usuarioId) {
      // Lo leyó en otra pestaña.
      const conversacion = conversacionesRef.current.find((item) => item.id === conversacionId);
      if (conversacion && (conversacion.ultimoMensaje?.id || 0) <= ultimoLeido) {
        setConversaciones((previas) => previas.map((item) => (item.id === conversacionId ? { ...item, noLeidos: 0 } : item)));
      } else {
        programarRecarga();
      }
    }
    setHilo((previo) => (previo?.conversacionId === conversacionId
      ? {
        ...previo,
        participantes: previo.participantes.map((participante) => (participante.id === quien
          ? { ...participante, ultimoLeido: Math.max(participante.ultimoLeido || 0, ultimoLeido) }
          : participante)),
      }
      : previo));
  };

  const manejar = (tipo, datos) => {
    switch (tipo) {
      case "estado":
        setEstado(datos.estado);
        break;
      case "listo":
        setEnLinea(new Set(datos.enLinea || []));
        cargarConversaciones();
        if (hiloRef.current?.conversacionId) cargarHilo(hiloRef.current.conversacionId);
        break;
      case "mensaje":
        recibirMensaje(datos.mensaje);
        break;
      case "leido":
        recibirLeido(datos);
        break;
      case "conversacion":
        programarRecarga();
        if (hiloRef.current?.conversacionId === datos.conversacionId) cargarHilo(datos.conversacionId);
        break;
      case "escribiendo":
        setEscribiendo((previo) => ({
          ...previo,
          [datos.conversacionId]: { usuarioId: datos.usuario.id, nombre: datos.usuario.nombre, hasta: Date.now() + ESCRIBIENDO_DURA_MS },
        }));
        break;
      case "presencia":
        setEnLinea((previo) => {
          const siguiente = new Set(previo);
          if (datos.enLinea) siguiente.add(datos.usuarioId);
          else siguiente.delete(datos.usuarioId);
          return siguiente;
        });
        break;
      case "en_linea":
        setEnLinea(new Set(datos.enLinea || []));
        break;
      default:
        break;
    }
  };
  // La conexión se crea una vez; así siempre llama a la versión con el estado al día.
  const manejarRef = useRef(manejar);
  manejarRef.current = manejar;

  useEffect(() => {
    if (!usuarioId) return undefined;
    cargarConversaciones();
    const conexion = crearConexion({
      obtenerToken: getSessionToken,
      alEvento: (tipo, datos) => manejarRef.current(tipo, datos),
    });
    conexionRef.current = conexion;
    return () => {
      conexionRef.current = null;
      clearTimeout(recargaRef.current);
      conexion.cerrar();
    };
  }, [usuarioId, crearConexion, cargarConversaciones]);

  useEffect(() => {
    if (!usuarioId || estado === "conectado") return undefined;
    const intervalo = setInterval(cargarConversaciones, INTERVALO_SIN_CONEXION_MS);
    return () => clearInterval(intervalo);
  }, [usuarioId, estado, cargarConversaciones]);

  // "Escribiendo…" se apaga solo si no vuelve a llegar.
  useEffect(() => {
    if (!Object.keys(escribiendo).length) return undefined;
    const temporizador = setTimeout(() => {
      const ahora = Date.now();
      setEscribiendo((previo) => Object.fromEntries(Object.entries(previo).filter(([, item]) => item.hasta > ahora)));
    }, 1000);
    return () => clearTimeout(temporizador);
  }, [escribiendo]);

  const noLeidos = useMemo(
    () => conversaciones.reduce((total, conversacion) => total + (conversacion.noLeidos || 0), 0),
    [conversaciones]
  );

  return {
    usuarioId,
    conversaciones,
    listaCargada,
    noLeidos,
    estado,
    enLinea,
    escribiendo,
    hilo,
    usuarios,
    abrir,
    abrirCon,
    cerrarHilo,
    cargarAnteriores,
    enviar,
    enviarArchivos,
    reintentar,
    descartar,
    avisarEscribiendo,
    cargarUsuarios,
    crearGrupo,
    agregarAGrupo,
    salirDeGrupo,
  };
}
